import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    Signal,
    signal,
    viewChild,
    WritableSignal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Event, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs';

import { RoleListBloc } from '@connect-services/client/blocs/role-list.bloc';
import { ManagementListHeaderComponent } from '@connect-services/client/components/management/management-list-header/management-list-header.component';
import { RoleCreateModalComponent } from '@connect-services/client/components/management/users-roles/create-modal/role-create-modal/role-create-modal.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { RouteCompletePathEnum } from '@connect-services/client/models/routes.model';
import { PaginationSdkParams } from '@connect-services/client/services/fetch-all.service';
import { GlobalSearchService } from '@connect-services/client/services/global-search.service';
import { OrganizationAccessPermissionsService } from '@connect-services/client/services/organization-access-permissions.service';
import { TableService } from '@connect-services/client/services/table.service';
import { TranslateService } from '@connect-services/client/services/translate.service';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import {
    CustomColumn,
    CustomTableComponent,
    SortState,
} from '@connect-services/src/lib/components/table/table.component';
import { Role, RoleControllerGet200Response } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { defaultPaginationParameters, OrderEnum, PaginationParameters } from '@shared/client/models/pagination.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CustomTableComponent,
        ManagementListHeaderComponent,
        NoDataToDisplayComponent,
        PaginatorComponent,
        RessourcesModule,
    ],
    selector: 'app-roles-list',
    styleUrl: './roles-list.component.scss',
    templateUrl: './roles-list.component.html',
})
export class RolesListComponent {
    public readonly paginatedRoles: WritableSignal<RoleControllerGet200Response | null> = signal(null);
    public paginationParameters: PaginationParameters = {
        ...defaultPaginationParameters,
        take: 5,
    };
    public readonly roleInPanel: WritableSignal<string | null> = signal<string | null>(null);
    public readonly rolesToDisplay: WritableSignal<Role[] | null> = signal<Role[] | null>(null);

    protected readonly canCreateRole: Signal<boolean> = computed(() =>
        this.organizationAccessPermissionsService.canManageRole(),
    );
    protected readonly roleDetailPanelOutletRegex: RegExp = /\(detailPanel:role\/([a-zA-Z0-9-]+)(?:\/edition)?\)/;
    protected readonly roleListBloc = inject(RoleListBloc);
    protected readonly router = inject(Router);
    protected tableColumns: CustomColumn[] = [];
    protected readonly translateService = inject(TranslateService);

    private readonly globalSearchService = inject(GlobalSearchService);
    private readonly matDialog = inject(MatDialog);
    private readonly organizationAccessPermissionsService = inject(OrganizationAccessPermissionsService);
    private readonly paginator: Signal<PaginatorComponent | undefined> = viewChild<PaginatorComponent>('paginator');
    private readonly tableService = inject(TableService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.tableColumns = [
            {
                key: 'name',
                label: this.translocoService.translate('role', { COUNT: 1 }),
            },
            {
                key: 'createdAt',
                label: 'createdAt',
            },
            {
                key: 'maxNumberOfAssignments',
                label: 'maxAllowed',
            },
            {
                key: 'assigned',
                label: 'numberOfRoleAssigned',
                sortable: false,
            },
        ];

        this.togglePanel();

        this.router.events.pipe(filter((event: Event) => event instanceof NavigationEnd)).subscribe(() => {
            this.togglePanel();
        });

        effect(() => {
            if (!this.roleListBloc.value()) {
                return;
            }

            this.paginatedRoles.set(this.roleListBloc.value());

            const roleWithAssignmentCount = [];
            for (const role of this.roleListBloc.value()!.items) {
                roleWithAssignmentCount.push({
                    ...role,
                    createdAt: new Date(role.createdAt),
                });
            }

            this.rolesToDisplay.set(roleWithAssignmentCount);
        });
    }

    public async onPaginationChange(page: number): Promise<void> {
        const pageInformation: PaginationSdkParams = {
            page: page,
            take: this.paginatedRoles()!.take,
        };

        this.paginationParameters = this.tableService.onPaginationChange(pageInformation, this.paginationParameters);

        await this.loadRoleListBloc();
    }

    public async onSortChange(sortState?: SortState): Promise<void> {
        if (!sortState) {
            return;
        }

        sortState = {
            ...sortState,
            order: sortState.order ?? OrderEnum.ASC,
        };

        this.paginationParameters = this.tableService.onSortChange(sortState, this.paginationParameters);

        await this.loadRoleListBloc();
        this.paginator()?.resetPaginator();
    }

    public openCreateRoleModal(): void {
        if (!this.canCreateRole()) {
            return;
        }

        this.matDialog.open(RoleCreateModalComponent, {});
    }

    public async toggleDetailPanel(role: Role): Promise<void> {
        if (!this.organizationAccessPermissionsService.canAccessRole()) {
            return;
        }

        const panelParam = this.roleInPanel() && role.id === this.roleInPanel() ? null : role.id;

        await this.router.navigate([
            ...RouteCompletePathEnum.USERS_ROLES,
            { outlets: { detailPanel: panelParam ? ['role', panelParam] : null } },
        ]);
    }

    private async loadRoleListBloc(searchValue?: string): Promise<void> {
        await this.roleListBloc.load(
            {
                ...this.paginationParameters,
                ...(searchValue && { properties: this.globalSearchService.columnsToSearchValueIn }),
                value: searchValue,
            },
            { refresh: true },
        );
    }

    private togglePanel(): void {
        if (this.router.url.match(this.roleDetailPanelOutletRegex)) {
            this.roleInPanel.set(this.router.url.match(this.roleDetailPanelOutletRegex)![1]);

            return;
        }

        this.roleInPanel.set(null);
    }
}
